<template>
  <CookieModal />
  <div class="contentContainer grow" :style="{ 'margin-bottom': `${bottomMargin - 60}px` }">
    <div class="title-area">
      <h1 class="h bold mb-1">{{ $t("bnpl:prescore.welcome") }}</h1>
      <p>
        {{ $t("bnpl:prescore.welcomeMessage") }}&nbsp;
        <span class="bold">
          {{ $t("bnpl:prescore.welcomeMessageOnlyFirst") }}
        </span>
      </p>
    </div>
    <div class="main-area flex-column gap-14">
      <p class="bold has-text-size-14">
        {{ $t("bnpl:prescore.login.title") }}
      </p>
      <div class="whitebox flex-column justify-content-center align-items-end padding16 gap16">
        <div class="flex-row align-items-start padding0 gap16 stretch">
          <div class="flex-column align-items-start padding0 gap8 grow">
            <div class="flex-row align-items-center justify-content-space-between padding0 gap32 stretch">
              <div class="flex-row align-items-center padding0 gap16">
                <div
                  class="flex-row justify-content-center align-items-center padding0"
                  style="width: 56px; height: 56px"
                >
                  <IllustrationsCart />
                </div>
                <div v-if="!isMobile || !isAmountEditable" class="flex-column align-items-start padding0">
                  <h2 class="h bold is-mobile-text-16">
                    {{ cart.name }}
                  </h2>
                  <p class="p-small gray600">
                    {{ cart.description }}
                  </p>
                </div>
              </div>
              <div class="flex-row align-items-center padding0 gap8 max-content">
                <div v-if="isAmountEditable" class="money-input">
                  <input
                    type="text"
                    ref="amountInputRef"
                    class="textInput"
                    id="amount"
                    v-model="amount"
                    @keydown.enter="editAmount"
                    @keydown="preventWordInput"
                    @focusout.prevent="editAmount"
                  />
                </div>
                <template v-else>
                  <h2 class="h bold is-mobile-text-16 max-content">{{ $n(parseFloat(amount), "currency") }}</h2>
                  <IconsEdit @click="editAmount" class="edit-icon" />
                </template>
              </div>
            </div>
          </div>
        </div>
      </div>
      <p class="bold has-text-size-14">
        {{ $t("bnpl:prescore.login.cards.title") }}
      </p>
      <div class="info-cards mb-1">
        <USPCard
          :icon="$t('bnpl:prescore.login.card1.icon')"
          :title="$t('bnpl:prescore.login.card1.title')"
          :description="$t('bnpl:prescore.login.card1.description')"
        />
        <USPCard
          :icon="$t('bnpl:prescore.login.card2.icon')"
          :title="$t('bnpl:prescore.login.card2.title')"
          :description="$t('bnpl:prescore.login.card2.description')"
        />
      </div>
      <p class="bold has-text-size-14">
        {{ $t("bnpl:prescore.login.reviews.title") }}
      </p>
      <LoginReview />
    </div>
    <div class="login-area hide-on-mobile">
      <div class="whitebox-shadowed padding32 gap18">
        <PaymentLogin loginTitle="bnpl:prescore.login.title" loginDescription="bnpl:prescore.login.description" />
      </div>
    </div>
    <div class="errorToast" :class="{ activeErrorToast: showErrorToast }">
      {{
        $t(popupErrorMessageToken, "Nemlehet a {min} és {max} értékeken kívül", {
          min: offer.minAmount,
          max: offer.maxAmount,
        })
      }}
    </div>

    <div class="flex-column align-items-center padding0 gap16 stretch"></div>
  </div>
  <div class="mobile-fixed hide-on-desktop whitebox-shadowed p-3 gap18" ref="mobileLogin">
    <p>{{ $t("bnpl:login.help_text") }}</p>
    <PaymentLogin />
  </div>
</template>

<script setup>
import { useI18n } from "vue-i18n";
import { useCartStore } from "~/stores/cart";
import { usePrescoreStore } from "~~/stores/prescore";
const cartStore = useCartStore();
const prescoreStore = usePrescoreStore();

const i18n = useI18n();

const isMobile = ref(false);

const cart = computed(() => cartStore.cart);
const offer = computed(() => prescoreStore.offer);

const popupErrorMessageToken = ref("");
const showErrorToast = ref(false);

const mobileLogin = ref(null);

const bottomMargin = computed(() => (mobileLogin.value ? mobileLogin.value.offsetHeight : 0));

const isAmountEditable = ref(true);

const amountInputRef = ref(null);

const amount = ref(null);

const preventWordInput = (e) => {
  if (!/(\d|Backspace|Delete|Arrow.*|\.|,)/gi.test(e.key)) {
    e.preventDefault();
  }
};

const editAmount = (event) => {
  if (cartStore.cart.sum) {
    if (cartStore.cart.sum < offer.value.minAmount) {
      amount.value = offer.value.minAmount;
      amountInputRef.value.focus();
      isAmountEditable.value = true;
      popupErrorToast("bnpl:popup.message.min");
    } else if (cartStore.cart.sum > offer.value.maxAmount) {
      amount.value = offer.value.maxAmount;
      amountInputRef.value.focus();
      isAmountEditable.value = true;
      popupErrorToast("bnpl:popup.message.max");
    } else {
      if (event.type === "focusout") {
        isAmountEditable.value = false;
      } else {
        isAmountEditable.value = !isAmountEditable.value;
      }
    }
  } else {
    isAmountEditable.value = true;
  }
};

watch(
  () => amountInputRef.value,
  (inputRef) => {
    if (inputRef) {
      inputRef.focus();
    }
  }
);

watch(
  amount,
  (newValue) => {
    cartStore.cart.sum = newValue;
  },
);

const popupErrorToast = (messageToken) => {
  popupErrorMessageToken.value = messageToken;
  showErrorToast.value = true;
  setTimeout(() => {
    showErrorToast.value = false;
  }, 3000);
};

onMounted(function () {
  if (window.matchMedia("only screen and (max-width: 792px)").matches) {
    isMobile.value = true;
  }
  if (!cartStore.cart.sum) {
    amountInputRef.value.focus();
  } else {
    amount.value = parseFloat(cartStore.cart.sum);
    isAmountEditable.value = false;
  }
});
</script>

<style lang="scss" scoped>
.contentContainer {
  position: relative;
  display: grid;
  grid-template-areas: "title empty" "main login";
  grid-template-rows: min-content auto;
  grid-template-columns: auto min-content;
  align-items: start;
  justify-items: stretch;
  max-width: 1000px;
  gap: 32px;
}

.main-area {
  grid-area: main;
}

.login-area {
  grid-area: login;
  position: sticky;
  top: 92px;
}

.title-area {
  grid-area: title;
  align-self: stretch;
}

.whitebox-shadowed {
  border-radius: 8px;
  box-shadow: 0px 4px 20px 0px rgba(0, 0, 0, 0.02);
  padding: 24px 16px;
  display: flex;
  flex-direction: column;
  gap: 18px;
  min-width: 343px;
}

.info-cards {
  display: flex;
  flex-direction: row;
  gap: 14px;
}

.p-body {
  width: 100%;
}

.productRow {
  position: relative;
  width: calc(100vw - 24px);
}

.productContainer {
  overflow-x: scroll;
  position: relative;
  padding-bottom: 12px;
  width: calc(100vw - 24px);
}

/* Chrome, Safari, Edge, Opera */
input::-webkit-outer-spin-button,
input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

/* Firefox */
input[type="number"] {
  -moz-appearance: textfield;
}

input#amount {
  text-align: right;
}

.money-input {
  position: relative;

  // flex-shrink: 0;
  min-width: max-content;

  input {
    padding-right: calc(2ch + 12px);

    &:disabled {
      border-color: white;
    }
  }

  span {
    display: block;
    position: absolute;
    top: 50%;
    right: 12px;
    transform: translate(0%, -50%);
  }
}

svg.edit-icon {
  width: 18px;
  height: 18px;
  cursor: pointer;
}

.max-content {
  width: max-content;
}
.productCard {
  width: 188px;
  position: relative;
  isolation: isolate;
  background: #ffffff;
  border-radius: 8px;
  z-index: -1;
}

.productName {
  width: 112px;
  text-align: center;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
}

.productImage {
  max-width: 62px;
  max-height: 62px;
}

.productImageContainer {
  text-align: center;
  width: 62px;
  height: 62px;
}

.productQuantity {
  padding: 4px 8px;
  background: var(--gray200);
  border-radius: 40px;
  position: absolute;
  right: 8px;
  top: 8px;
  font-style: normal;
  font-weight: 500;
  font-size: 12px;
  line-height: 12px;
  text-align: center;
}

.dots {
  display: flex;
  gap: 10px;
  padding: 16px 0;

  .dot {
    width: 8px;
    height: 8px;
    border-radius: 8px;
    background: var(--gray200);

    &.active {
      background: var(--brandPrimary);
    }
  }
}

.mobile-fixed {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  border-bottom-left-radius: 0;
  border-bottom-right-radius: 0;
  box-shadow: 0px -4px 20px 0px rgba(0, 0, 0, 0.1);
  z-index: 1;
}

@media only screen and (max-width: 792px) {
  .hide-on-mobile {
    display: none;
  }

  .is-mobile-text-16 {
    font-size: 16px;
  }

  svg.edit-icon {
    width: 14px;
    height: 14px;
    cursor: pointer;
  }

  .contentContainer {
    grid-template-areas: "title" "main" "login";
    grid-template-rows: min-content auto;
    grid-template-columns: auto;
    margin-bottom: 300px;
  }
}

@media only screen and (min-width: 793px) {
  .productContainer {
    -ms-overflow-style: none;
    /* Internet Explorer 10+ */
    scrollbar-width: none;
    /* Firefox */
    padding-bottom: 0;
  }

  .productContainer::-webkit-scrollbar {
    display: none;
    /* Safari and Chrome */
  }

  .hide-on-desktop {
    display: none;
  }
}
</style>
